/**
 * WallReadyPage — the room-becomes-alive moment.
 *
 * Two beats:
 *   1. Before any room reaction: "Bring the room together ✦"
 *      • QR plate (resolves to /api/events/:id/scan → /memory-snap)
 *      • Share row: QR (visual anchor) + WhatsApp + Copy link + Native share
 *      • Wallet pass affordance: "Keep the night close ✦" — visual only,
 *        intentionally incomplete, no fake download, no broken state
 *      • Quiet self-upload affordance
 *   2. After the first snap lands: "The room has started breathing ✦"
 *      • QR plate reduces prominence ~18%
 *      • Dust motes warmer
 *      • Wall preview subtly brightens
 *      • Share pills soften into secondary contrast
 *
 * `first_room_reaction_at` is server-authored (immutable, set on first
 * QR scan OR first share interaction). The frontend pings
 * /api/events/:id/room-reaction/share when the host taps share pills.
 *
 * NO toasts. NO counters. NO success banners. The page is one panel
 * holding the activation moment, then softening into the breathing
 * state when the room begins gathering.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAPER_BG  = '#1c1108';
const PAPER_HI  = '#2a190a';
const INK       = '#FFE7A8';
const INK_MUTED = 'rgba(255,235,210,0.62)';
const GOLD      = '#D4AF5E';
const GOLD_DARK = '#B8923F';
const HAIR      = 'rgba(212,170,82,0.32)';
const HAIR_SOFT = 'rgba(212,170,82,0.20)';

const SERIF = 'Georgia, "Times New Roman", serif';

export default function WallReadyPage() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [snapCount, setSnapCount] = useState(0);
  const [firstSnapAt, setFirstSnapAt] = useState(null);
  const [firstReactionAt, setFirstReactionAt] = useState(null);
  const [pressed, setPressed] = useState({ qr: false, whatsapp: false, copy: false, native: false, wallet: false });
  const [copyState, setCopyState] = useState('idle');
  const [shareState, setShareState] = useState('idle');
  const pollRef = useRef(null);
  const startedAt = useRef(Date.now());

  // ── Load event + start polling ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const lookup = sessionStorage.getItem(`begin_started_${eventId}`);
    if (lookup) startedAt.current = parseInt(lookup, 10);

    axios.get(`${API}/events/by-id/${eventId}/begin-info`)
      .then((r) => {
        if (cancelled) return;
        setEvent(r.data);
        if (r.data?.first_room_reaction_at) setFirstReactionAt(r.data.first_room_reaction_at);
      })
      .catch(() => { if (!cancelled) setLoadError("That night couldn't be opened."); });

    const poll = async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/snap-count`);
        if (cancelled) return;
        const count = r.data?.snap_count || 0;
        setSnapCount(count);
        if (count > 0 && r.data?.first_snap_at) setFirstSnapAt(r.data.first_snap_at);
        if (r.data?.first_room_reaction_at) setFirstReactionAt(r.data.first_room_reaction_at);
      } catch {/* keep trying silently */}
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [eventId]);

  const wallUrl = event ? `${window.location.origin}/event/${event.slug}/memory-wall` : '';
  const snapUrl = event ? `${window.location.origin}/event/${event.slug}/memory-snap` : '';
  const qrSrc   = event ? `${API}/events/${eventId}/qr.png?target=memory-snap` : '';

  // Fire-and-forget — never block UI if the counter ping fails.
  const pingReaction = (channel) => {
    axios.post(`${API}/events/${eventId}/room-reaction/share`, { channel })
      .then((r) => { if (r.data?.first_room_reaction_at) setFirstReactionAt(r.data.first_room_reaction_at); })
      .catch(() => {/* never throws to the host */});
  };

  const onPressDown = (key) => setPressed((p) => ({ ...p, [key]: true }));
  const onPressUp   = (key) => setPressed((p) => ({ ...p, [key]: false }));

  const handleCopyLink = async () => {
    pingReaction('copy');
    try {
      await navigator.clipboard.writeText(snapUrl);
      setCopyState('copied');
      setTimeout(() => setCopyState('idle'), 2400);
    } catch {
      setCopyState('manual');
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) { handleCopyLink(); return; }
    pingReaction('native');
    try {
      await navigator.share({
        title: event?.event_name || 'A night to preserve',
        text: 'Tap here to add a memory ✦',
        url: snapUrl,
      });
      setShareState('shared');
      setTimeout(() => setShareState('idle'), 2400);
    } catch {/* user cancelled — silent */}
  };

  const handleWhatsApp = () => {
    pingReaction('whatsapp');
    const body = `Tap here to add a memory ✦ ${snapUrl}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(body)}`;
    window.open(wa, '_blank', 'noopener');
  };

  const activated     = snapCount > 0;          // first snap has landed
  const roomReacted   = !!firstReactionAt;       // first scan or share fired
  const secondsToActivation = (firstSnapAt && startedAt.current)
    ? Math.max(1, Math.round((new Date(firstSnapAt).getTime() - startedAt.current) / 1000))
    : null;

  if (loadError) return <Shell><Centered>{loadError}</Centered></Shell>;
  if (!event)    return <Shell><Centered>Opening the room…</Centered></Shell>;

  // ── ACTIVATED — the first memory has landed ──────────────────────
  if (activated) {
    return (
      <Shell breathing>
        <div data-testid="wall-ready-activated" style={{
          textAlign: 'center', padding: 'clamp(36px, 6vw, 64px) 16px',
          maxWidth: 620, margin: '0 auto',
        }}>
          <Eyebrow>✦ The wall is alive</Eyebrow>
          <h1 style={{
            fontFamily: SERIF, fontWeight: 500,
            fontSize: 'clamp(30px, 5.4vw, 44px)', letterSpacing: '-0.01em',
            margin: '14px 0 0', color: INK, lineHeight: 1.1,
          }}>The first memory has landed.</h1>
          <p style={{ ...italicStyle, fontSize: 16, color: INK_MUTED, marginTop: 16, lineHeight: 1.6 }}>
            From here, every guest who scans adds another moment. The room takes care of itself.
          </p>

          {secondsToActivation !== null && secondsToActivation < 600 && (
            <div data-testid="wall-ready-activation-time" style={{
              marginTop: 26, fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13, color: 'rgba(212,170,82,0.62)', letterSpacing: '0.06em',
            }}>
              {`${secondsToActivation} seconds from naming to alive ✦`}
            </div>
          )}

          <div style={{ marginTop: 42, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 14 }}>
            <a
              href={`/event/${event.slug}/memory-wall`}
              data-testid="wall-ready-open-wall"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, height: 50,
                padding: '0 24px', borderRadius: 999,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
                color: '#0F0B1A', textDecoration: 'none',
                fontFamily: SERIF, fontWeight: 700, fontSize: 14.5,
              }}
            >Step into the wall ✦</a>
            <button
              type="button"
              onClick={handleNativeShare}
              data-testid="wall-ready-share-after"
              style={{
                background: 'transparent', border: `1px solid ${HAIR}`,
                borderRadius: 999, color: INK, height: 50, padding: '0 22px',
                fontFamily: SERIF, fontStyle: 'italic', fontSize: 14, cursor: 'pointer',
              }}
            >{shareState === 'shared' ? 'Shared ✦' : 'Share the wall'}</button>
          </div>
        </div>
      </Shell>
    );
  }

  // ── PRE-ACTIVATION — the room is waiting / starting to react ────
  // When `roomReacted` is true the surface softens: QR scaled to ~82%
  // (the -18% prominence Craig specified), share pills muted into
  // secondary contrast, dust motes warmer, wall preview brighter.
  return (
    <Shell breathing={roomReacted}>
      <div style={{ maxWidth: 780, margin: '0 auto', padding: 'clamp(28px, 4vw, 48px) clamp(16px, 4vw, 28px) 48px' }}>
        <Eyebrow>✦ {event.event_name}</Eyebrow>
        <h1 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(28px, 5vw, 42px)', letterSpacing: '-0.01em',
          margin: '14px 0 0', color: INK, lineHeight: 1.08,
          transition: 'color 1600ms ease',
        }} data-testid="wall-ready-title">
          {roomReacted ? 'The room has started breathing ✦' : 'Bring the room together ✦'}
        </h1>
        <p data-testid="wall-ready-subtitle" style={{
          ...italicStyle, fontSize: 'clamp(15px, 1.5vw, 17px)',
          color: INK_MUTED, marginTop: 16, lineHeight: 1.6,
          transition: 'opacity 1200ms ease',
        }}>
          {roomReacted
            ? 'Someone has reached in. The first memory will land here as soon as anyone uploads.'
            : 'Hand this QR to the room. Or send the link. The first memory will land here as soon as anyone uploads.'}
        </p>

        {/* ── Share surface: QR plate + share row ──────────────────────── */}
        <div data-testid="wall-ready-share-surface" style={{
          marginTop: 36,
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 22,
          alignItems: 'start',
        }}>
          {/* QR plate — large, scannable. Scales to ~82% once the room has reacted. */}
          <div data-testid="wall-ready-qr-card" style={{
            background: 'rgba(20,12,6,0.7)', borderRadius: 18,
            border: `1px solid ${HAIR}`, padding: 22, textAlign: 'center',
            transform: roomReacted ? 'scale(0.82)' : 'scale(1)',
            transformOrigin: 'top center',
            opacity: roomReacted ? 0.78 : 1,
            transition: 'transform 1400ms ease, opacity 1400ms ease',
          }}>
            <div style={{ background: '#FAF6EE', borderRadius: 8, padding: 18, marginBottom: 14 }}>
              <img
                src={qrSrc}
                alt="Scan to add a memory"
                data-testid="wall-ready-qr-image"
                style={{ display: 'block', width: '100%', height: 'auto', maxWidth: 280, margin: '0 auto' }}
              />
            </div>
            <p style={{ ...italicStyle, fontSize: 13, color: INK_MUTED, margin: 0 }}>
              Scan to add a memory ✦
            </p>
          </div>

          {/* Share row + link + wallet affordance */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Pill row */}
            <div data-testid="wall-ready-share-row" style={{
              background: 'rgba(20,12,6,0.7)', borderRadius: 18,
              border: `1px solid ${HAIR}`, padding: 22,
              transition: 'opacity 1200ms ease',
              opacity: roomReacted ? 0.86 : 1,
            }}>
              <div style={{
                fontSize: 10, letterSpacing: '0.32em', textTransform: 'uppercase',
                color: GOLD, marginBottom: 12,
              }}>✦ Or send the link</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <SharePill
                  testId="wall-ready-whatsapp"
                  onClick={handleWhatsApp}
                  onPressDown={() => onPressDown('whatsapp')}
                  onPressUp={() => onPressUp('whatsapp')}
                  pressed={pressed.whatsapp}
                  soft={roomReacted}
                  label="WhatsApp"
                />
                <SharePill
                  testId="wall-ready-copy-pill"
                  onClick={handleCopyLink}
                  onPressDown={() => onPressDown('copy')}
                  onPressUp={() => onPressUp('copy')}
                  pressed={pressed.copy}
                  soft={roomReacted}
                  label={copyState === 'copied' ? 'Link copied ✦' : (copyState === 'manual' ? 'Long-press to copy' : 'Copy link')}
                />
                <SharePill
                  testId="wall-ready-native-pill"
                  onClick={handleNativeShare}
                  onPressDown={() => onPressDown('native')}
                  onPressUp={() => onPressUp('native')}
                  pressed={pressed.native}
                  soft={roomReacted}
                  label={shareState === 'shared' ? 'Shared ✦' : 'Share…'}
                />
              </div>
              <div data-testid="wall-ready-share-url" style={{
                background: '#0c0703', borderRadius: 10,
                border: `1px solid ${HAIR_SOFT}`, padding: '11px 14px',
                fontFamily: '"SFMono-Regular", "Menlo", monospace',
                fontSize: 12.5, color: INK, wordBreak: 'break-all',
              }}>{snapUrl}</div>
            </div>

            {/* Wallet pass — intentionally incomplete affordance */}
            <WalletPassAffordance
              eventName={event.event_name}
              eventDate={event.event_date}
              pressed={pressed.wallet}
              onPressDown={() => onPressDown('wallet')}
              onPressUp={() => onPressUp('wallet')}
            />
          </div>
        </div>

        {/* The "you upload first" affordance — quiet but central. */}
        <div data-testid="wall-ready-self-upload-card" style={{
          marginTop: 36, padding: 'clamp(22px, 3vw, 28px)',
          background: `linear-gradient(135deg, ${PAPER_HI} 0%, ${PAPER_BG} 100%)`,
          borderRadius: 18, border: `1px solid ${HAIR}`, textAlign: 'center',
        }}>
          <Eyebrow>✦ Or start it yourself</Eyebrow>
          <p style={{
            ...italicStyle, fontSize: 'clamp(15px, 1.6vw, 17px)',
            color: INK_MUTED, lineHeight: 1.6, margin: '14px auto 22px',
            maxWidth: 480,
          }}>
            Add the first memory now so the wall has something to react to when guests arrive.
          </p>
          <a
            href={`/event/${event.slug}/memory-snap`}
            data-testid="wall-ready-self-upload-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              height: 50, padding: '0 26px', borderRadius: 999,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_DARK} 100%)`,
              color: '#0F0B1A', textDecoration: 'none',
              fontFamily: SERIF, fontWeight: 700, fontSize: 14.5, letterSpacing: '0.02em',
            }}
          >Add the first memory ✦</a>
        </div>

        {/* Quiet live status — never a counter, never social. */}
        <div data-testid="wall-ready-live-status" style={{ textAlign: 'center', marginTop: 28 }}>
          <span style={{
            ...italicStyle, fontSize: 12.5,
            color: 'rgba(255,235,210,0.42)',
            letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
            <Pulse warm={roomReacted} /> {roomReacted ? 'The room is gathering' : 'Watching the wall'}
          </span>
        </div>
      </div>
    </Shell>
  );
}


// ── primitives ────────────────────────────────────────────────────

const Shell = ({ children, breathing = false }) => (
  <div data-testid="wall-ready-page" data-room-state={breathing ? 'gathering' : 'waiting'} style={{
    minHeight: '100vh',
    background: breathing
      ? `radial-gradient(ellipse at top, #321d0c 0%, ${PAPER_BG} 60%, #060403 100%)`
      : `radial-gradient(ellipse at top, ${PAPER_HI} 0%, ${PAPER_BG} 60%, #050302 100%)`,
    color: INK,
    transition: 'background 2200ms ease',
    position: 'relative',
    overflow: 'hidden',
  }}>
    <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@500&display=swap" rel="stylesheet" />
    <DustMotes warm={breathing} />
    <header style={{ padding: '14px 24px', position: 'relative', zIndex: 1 }}>
      <a href="/" style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 14, color: INK_MUTED,
        textDecoration: 'none', letterSpacing: '0.04em',
      }}>← EventDrop</a>
    </header>
    <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </div>
);

const Centered = ({ children }) => (
  <p style={{ ...italicStyle, color: INK_MUTED, padding: '120px 0', textAlign: 'center' }}
     data-testid="wall-ready-load-error">{children}</p>
);

const Eyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.22em',
    textTransform: 'uppercase', color: GOLD,
  }}>{children}</div>
);

const Pulse = ({ warm = false }) => (
  <span style={{
    display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
    background: warm ? '#E6C079' : GOLD, marginRight: 8, verticalAlign: 'middle',
    boxShadow: `0 0 0 0 ${GOLD}`,
    animation: 'wallReadyPulse 1.8s ease-out infinite',
  }}>
    <style>{`
      @keyframes wallReadyPulse {
        0%   { box-shadow: 0 0 0 0 rgba(212,175,94,0.45); opacity: 0.95; }
        70%  { box-shadow: 0 0 0 7px rgba(212,175,94,0); opacity: 0.55; }
        100% { box-shadow: 0 0 0 0 rgba(212,175,94,0); opacity: 0.95; }
      }
      @keyframes wallReadyDust {
        0%   { transform: translate3d(0, 0, 0); opacity: 0; }
        15%  { opacity: 0.55; }
        85%  { opacity: 0.4; }
        100% { transform: translate3d(40px, -120px, 0); opacity: 0; }
      }
    `}</style>
  </span>
);

const DustMotes = ({ warm }) => (
  <div aria-hidden style={{
    position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
  }}>
    {[12, 28, 44, 62, 78, 88].map((left, i) => (
      <span key={i} style={{
        position: 'absolute',
        left: `${left}%`,
        top: `${30 + (i * 11) % 60}%`,
        width: 3, height: 3, borderRadius: 999,
        background: warm ? 'rgba(255,212,150,0.62)' : 'rgba(255,224,176,0.42)',
        filter: 'blur(0.6px)',
        animation: `wallReadyDust ${18 + i * 3}s linear ${i * 1.4}s infinite`,
      }} />
    ))}
  </div>
);

// Share pill — onPointerDown tactile press, no hover lift, no glow.
// Settles inward on press (translateY +1px) and softens its border.
const SharePill = ({ testId, label, onClick, onPressDown, onPressUp, pressed, soft }) => {
  const baseBorder = soft ? HAIR_SOFT : HAIR;
  const pressedBorder = 'rgba(212,170,82,0.20)';
  return (
    <button
      type="button"
      data-testid={testId}
      data-pressed={pressed ? '1' : '0'}
      onClick={onClick}
      onPointerDown={onPressDown}
      onPointerUp={onPressUp}
      onPointerLeave={onPressUp}
      onPointerCancel={onPressUp}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: 40, padding: '0 16px', borderRadius: 999,
        background: 'rgba(18,12,6,0.55)',
        border: `1px solid ${pressed ? pressedBorder : baseBorder}`,
        color: soft ? 'rgba(255,235,210,0.74)' : INK,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 13.5, letterSpacing: '0.02em',
        cursor: 'pointer',
        transform: pressed ? 'translateY(1px)' : 'translateY(0)',
        transition: 'transform 220ms ease, border-color 220ms ease, color 600ms ease',
      }}
    >{label}</button>
  );
};

// Wallet pass — affordance only. "Keep the night close ✦" with quiet
// microcopy. NO download, NO fake .pkpass, NO dead button. Inert by
// design — Craig's "intentionally incomplete" lock. The card itself is
// rendered as a beautifully styled wallet-card silhouette; pressing it
// only settles inward and softens its border, nothing else.
const WalletPassAffordance = ({ eventName, eventDate, pressed, onPressDown, onPressUp }) => (
  <div
    data-testid="wall-ready-wallet-affordance"
    role="group"
    aria-label="Keep the night close — coming quietly soon"
    onPointerDown={onPressDown}
    onPointerUp={onPressUp}
    onPointerLeave={onPressUp}
    onPointerCancel={onPressUp}
    style={{
      position: 'relative', borderRadius: 22,
      padding: '20px 22px 18px',
      background: 'linear-gradient(135deg, #2a190a 0%, #1c1108 70%, #14090a 100%)',
      border: `1px solid ${pressed ? 'rgba(212,170,82,0.20)' : HAIR}`,
      boxShadow: '0 18px 36px rgba(0,0,0,0.45) inset, 0 8px 18px rgba(0,0,0,0.32)',
      transform: pressed ? 'translateY(1px)' : 'translateY(0)',
      transition: 'transform 220ms ease, border-color 220ms ease',
      cursor: 'default',
      overflow: 'hidden',
    }}
  >
    {/* corner ribbon */}
    <span aria-hidden style={{
      position: 'absolute', top: 12, right: 12,
      width: 22, height: 22, borderRadius: '50%',
      background: 'radial-gradient(circle at 30% 30%, #E6C079 0%, #B8923F 70%)',
      boxShadow: '0 0 14px rgba(212,170,82,0.18)',
    }} />
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 10.5, letterSpacing: '0.28em', textTransform: 'uppercase',
      color: 'rgba(212,170,82,0.78)',
    }} data-testid="wall-ready-wallet-eyebrow">✦ Keep the night close ✦</div>

    <div style={{
      marginTop: 12, fontFamily: SERIF, fontSize: 17.5,
      color: INK, lineHeight: 1.32,
    }}>{eventName || 'A night to preserve'}</div>

    {eventDate && (
      <div style={{
        marginTop: 4, fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 12.5, color: INK_MUTED, letterSpacing: '0.04em',
      }}>{eventDate}</div>
    )}

    {/* hairline */}
    <div aria-hidden style={{
      width: 38, height: 1, background: HAIR_SOFT,
      margin: '14px 0 12px',
    }} />

    <div data-testid="wall-ready-wallet-microcopy" style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12.5, color: 'rgba(255,235,210,0.52)',
      letterSpacing: '0.02em', lineHeight: 1.5,
    }}>
      Coming quietly soon ✦
    </div>
  </div>
);

const italicStyle = { fontFamily: SERIF, fontStyle: 'italic' };
